import classNames from 'classnames';
import { capitalize } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import { canDuplicateInitiative } from '../../../constants/initiativeDuplication.js';
import { selectInitiativeCopy } from '../../../modules/initiativeDuplications.js';
import { selectProjects } from '../../../modules/projects.js';
import { getOrgTermsContent } from '../../../content/utils.js';
import RightArrowIcon from '../../icons/RightArrowIcon.jsx';
import InitiativesTable from '../../initiatives/InitiativesTable/index.jsx';
import { NewInitiativeLink } from '../../links/index.jsx';
import SubHeader from '../../SubHeader.jsx';
import { PlusButtonIcon } from '../../icons/PlusButtonIcon.jsx';

const isCompleted = (initiative) => moment().isAfter(initiative.endDate, 'day');

// The copy just made goes right under its source instead of its usual place.
const placeCopyUnderSource = (initiatives, copy) => {
  const copied = initiatives.find(({ id }) => id === copy.id);
  const rest = initiatives.filter(({ id }) => id !== copy.id);
  const sourceIndex = rest.findIndex(({ id }) => id === copy.sourceId);

  if (!copied || sourceIndex === -1) return initiatives;

  rest.splice(sourceIndex + 1, 0, copied);

  return rest;
};

const mapStateToProps = (state) => ({
  projects: selectProjects(state),
  copy: selectInitiativeCopy(state),
});

const InitiativesSection = ({
  project,
  type,
  deleteInitiative,
  showCompleted,
  projects,
  copy,
}) => {
  const [initiativesHidden, setInitiativesHidden] = useState(true);

  const toggleInitiatives = useCallback((e) => {
    e.preventDefault();
    setInitiativesHidden((hidden) => !hidden);
  }, []);

  const tableContainerId = `${type}InitiativesTableContainer`;
  const typeCapitalized = capitalize(type);

  const listCopy =
    copy && copy.type === type && copy.projectId === project.id ? copy : null;

  const visibleInitiatives = project[`${type}Initiatives`]
    .filter((initiative) => !initiative.draft)
    .filter((initiative) => showCompleted || !isCompleted(initiative));

  const initiatives = listCopy
    ? placeCopyUnderSource(visibleInitiatives, listCopy)
    : visibleInitiatives;

  const className = classNames('initiatives-section', {
    'initiatives-hidden': initiativesHidden,
  });

  return (
    <section className={className}>
      <SubHeader flexJustify small>
        <div className="initiatives-heading">
          <h2>
            <a
              aria-controls={tableContainerId}
              aria-expanded={!initiativesHidden}
              className="flex"
              href="#"
              onClick={toggleInitiatives}
              role="button"
            >
              <RightArrowIcon className="collapse-caret" />
              <span className="initiative-type">{typeCapitalized}</span>
            </a>
          </h2>
          <strong className="bordered text-muted">
            Total:&nbsp;
            <span className="text-danger count">{initiatives.length}</span>
          </strong>
        </div>
        {project[
          `manage${type.charAt(0).toUpperCase() + type.slice(1)}Initiatives`
        ] ? (
          project.archived ? (
            <Button
              onClick={() =>
                window.alert(getOrgTermsContent('archivedProjectMessage'))
              }
              bsStyle="success"
            >
              <PlusButtonIcon />
              Add {typeCapitalized}
            </Button>
          ) : (
            <NewInitiativeLink projectId={project.id} initiativeType={type}>
              <Button bsStyle="success">
                <PlusButtonIcon />
                Add {typeCapitalized}
              </Button>
            </NewInitiativeLink>
          )
        ) : null}
      </SubHeader>
      <div id={tableContainerId}>
        {!initiativesHidden && (
          <InitiativesTable
            initiatives={initiatives}
            initiativeType={type}
            isArchived={project.archived}
            deleteInitiative={deleteInitiative}
            project={project}
            canDuplicate={canDuplicateInitiative(type, project, projects)}
            newCopyId={listCopy?.id}
            manageProjectEntities={
              project[
                `manage${type.charAt(0).toUpperCase() + type.slice(1)}Initiatives`
              ]
            }
          />
        )}
      </div>
    </section>
  );
};

InitiativesSection.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  type: PropTypes.string.isRequired,
  showCompleted: PropTypes.bool,
};

InitiativesSection.defaultProps = {
  showCompleted: false,
};

export default connect(mapStateToProps)(InitiativesSection);
